/**
 * As regras que valeram nesta grade. P6 (`PESQUISA-ESPM.md` §24.7).
 *
 * *"Não tenho nenhum relatório que me diga quais regras que eu estou
 * trabalhando no horário. Às vezes dá um erro e eu não descubro."* — a
 * coordenação, sobre o URÂNIA, em 30/10.
 *
 * **Recolhido por padrão.** É consulta, não o assunto da tela: abre-se quando
 * uma aula está num lugar estranho e a pergunta é "por quê?".
 *
 * **O que é deduzido aparece marcado.** Uma regra que o sistema tirou do turno
 * da disciplina ou de uma frase de observação da secretaria é exatamente a que
 * ela precisa conferir — a que ninguém digitou.
 */

import { useEffect, useMemo, useState } from 'react'

import { ErroApi, horario as api, type RegrasDaGrade } from '../api'

/** Quantas linhas um grupo mostra antes de pedir busca. */
const LIMITE = 60

const semAcento = (s: string) =>
  s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

export function Regras({ projeto, cenario, versao }: { projeto: string; cenario: string; versao: unknown }) {
  const [dados, setDados] = useState<RegrasDaGrade | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  // `versao` muda quando a grade é relida — assim o inventário acompanha um
  // processamento novo sem a tela precisar saber o que mudou.
  useEffect(() => {
    let vivo = true
    api
      .regras(projeto, cenario)
      .then((d) => vivo && (setDados(d), setErro(null)))
      .catch((e) => vivo && setErro(e instanceof ErroApi ? e.message : 'não foi possível ler as regras'))
    return () => {
      vivo = false
    }
  }, [projeto, cenario, versao])

  if (erro) return <p className="field__hint">{erro}</p>
  if (!dados || dados.estado === 'NAO_PROCESSADO') return null

  return (
    <details className="regras no-print" data-tour="regras">
      <summary>
        <strong>As regras desta grade</strong>
        {dados.estado === 'OK' && (
          <span className="field__hint"> · {dados.total} regras que o motor teve de cumprir</span>
        )}
      </summary>

      {dados.estado === 'SEM_INVENTARIO' ? (
        <p className="field__hint">
          Esta grade foi montada antes de o sistema guardar a lista de regras. Processe de novo para
          vê-la.
        </p>
      ) : (
        <div className="stack">
          {dados.grupos.map((g) => (
            <Grupo key={g.tipo} grupo={g} />
          ))}
        </div>
      )}
    </details>
  )
}

function Grupo({ grupo }: { grupo: RegrasDaGrade['grupos'][number] }) {
  const [busca, setBusca] = useState('')
  const [soDeduzidas, setSoDeduzidas] = useState(false)

  const visiveis = useMemo(() => {
    const q = semAcento(busca.trim())
    return grupo.regras.filter(
      (r) =>
        (!soDeduzidas || r.origem !== 'DECLARADA') &&
        (!q || semAcento(`${r.descricao} ${r.valor_atual}`).includes(q)),
    )
  }, [grupo, busca, soDeduzidas])

  return (
    <details className="regras__grupo">
      <summary>
        {grupo.titulo} <span className="mono">({grupo.quantidade})</span>
        {grupo.deduzidas > 0 && (
          <span className="regras__deduzida"> · {grupo.deduzidas} deduzida(s) pelo sistema</span>
        )}
      </summary>
      <p className="field__hint">{grupo.explicacao}</p>

      {(grupo.regras.length > 8 || grupo.deduzidas > 0) && (
        <div className="filtro">
          {grupo.regras.length > 8 && (
            <input
              className="input filtro__campo"
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Procurar — nome, turma, dia"
              aria-label={`Procurar em ${grupo.titulo}`}
            />
          )}
          {grupo.deduzidas > 0 && (
            <label className="row">
              <input type="checkbox" checked={soDeduzidas} onChange={(e) => setSoDeduzidas(e.target.checked)} />
              só as deduzidas
            </label>
          )}
          <span className="filtro__conta">
            {visiveis.length} de {grupo.quantidade}
          </span>
        </div>
      )}

      <ul className="regras__lista">
        {visiveis.slice(0, LIMITE).map((r) => (
          <li key={r.id}>
            {r.descricao}
            {r.origem !== 'DECLARADA' && (
              <span
                className="regras__deduzida"
                title="O sistema deduziu esta regra do turno da disciplina, do dia fixo ou de uma observação da planilha — ninguém a digitou."
              >
                {r.origem === 'DEDUZIDA' ? ' · deduzida' : ' · declarada e deduzida'}
              </span>
            )}
          </li>
        ))}
      </ul>
      {visiveis.length > LIMITE && (
        <p className="field__hint">
          E mais {visiveis.length - LIMITE}. Use a busca para achar a que procura.
        </p>
      )}
    </details>
  )
}
